import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, SlidersHorizontal, Phone, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useMerchantMarket } from '../hooks/useMerchantMarket';
import LargeNumberCard from '../components/marketplace/LargeNumberCard';
import MarketHero from '../components/marketplace/MarketHero';
import MarketFilterShell, { FilterField } from '../components/marketplace/MarketFilterShell';
import EmptyState from '../components/marketplace/EmptyState';
import { pageBg, inputCls, selectCls } from '../components/marketplace/frames';
import { startsWith, endsWith, containsAll, hasPattern, PATTERNS_AR, PATTERNS_EN } from '../utils/numberFilters';
import { usePhoneOperators } from '../hooks/usePhoneOperators';
import { operatorView } from '../constants/operators';

export default function MobileNumbers() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const navigate = useNavigate();
  const { items, loading, error } = useMerchantMarket('phone_numbers', { limit: 300 });
  const { operators } = usePhoneOperators();

  const [q, setQ] = useState('');
  const [operator, setOperator] = useState('all');
  const [code, setCode] = useState('');
  const [sort, setSort] = useState('newest');
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [contains, setContains] = useState('');
  const [pattern, setPattern] = useState('any');
  const [advOpen, setAdvOpen] = useState(false);

  const selectedOp = operators.find((o) => o.id === operator);
  const codeOptions = useMemo(
    () => (selectedOp ? selectedOp.codes : [...new Set(operators.flatMap((o) => o.codes))].sort()),
    [selectedOp, operators]
  );

  const changeOperator = (id) => {
    setOperator(id);
    const op = operators.find((o) => o.id === id);
    if (op && code && !op.codes.includes(code)) setCode('');
  };

  const filtered = useMemo(() => {
    let list = items;
    if (selectedOp) list = list.filter((i) => operatorView(i, operators).id === selectedOp.id);
    if (code) list = list.filter((i) => startsWith(i, code));
    if (q) {
      const qq = q.replace(/\D/g, '');
      list = list.filter(
        (i) =>
          (i.number || '').includes(qq) ||
          (i.displayNumber || '').includes(qq) ||
          (i.description || '').toLowerCase().includes(q.toLowerCase()) ||
          (i.store?.name || '').toLowerCase().includes(q.toLowerCase())
      );
    }
    if (prefix)   list = list.filter((i) => startsWith(i, prefix));
    if (suffix)   list = list.filter((i) => endsWith(i, suffix));
    if (contains) list = list.filter((i) => containsAll(i, contains));
    if (pattern && pattern !== 'any') list = list.filter((i) => hasPattern(i, pattern));

    if (sort === 'newest')
      list = [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (sort === 'price_asc')
      list = [...list].filter((i) => !i.priceOnRequest).sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sort === 'price_desc')
      list = [...list].filter((i) => !i.priceOnRequest).sort((a, b) => (b.price || 0) - (a.price || 0));
    return list;
  }, [items, q, selectedOp, operators, code, sort, prefix, suffix, contains, pattern]);

  const hasAnyFilter = q || operator !== 'all' || code || sort !== 'newest' || prefix || suffix || contains || pattern !== 'any';
  const patternOptions = isAr ? PATTERNS_AR : PATTERNS_EN;

  const clearAll = () => {
    setQ(''); setOperator('all'); setCode(''); setSort('newest');
    setPrefix(''); setSuffix(''); setContains(''); setPattern('any');
  };

  const openDetails = (item) => navigate(`/phone/${item.id}`, { state: { store: item.store, item } });
  const inp = inputCls(isDarkMode);
  const sel = selectCls(isDarkMode);

  return (
    <div className={`min-h-screen ${pageBg(isDarkMode)}`} dir={isAr ? 'rtl' : 'ltr'}>
      <MarketHero
        isDarkMode={isDarkMode}
        isAr={isAr}
        icon={Phone}
        eyebrowAr="أرقام مميّزة موثّقة من كل المشغّلين"
        eyebrowEn="Verified premium numbers from every operator"
        titleAr="أرقام موبايل"
        titleAccentAr="مميزة ونادرة"
        titleEn="Premium Mobile"
        titleAccentEn="Numbers — VIP"
        subtitleAr="تصفّح أكبر تشكيلة من أرقام VIP في الإمارات — رباعية، ثلاثية، متكررة ومرآة. الأرقام المخفية تظهر بحروف X و Y وتُكشف عند التواصل."
        subtitleEn="Browse the widest UAE VIP number collection — quads, triples, mirrors and repeats. Hidden digits show as X and Y and are revealed on contact."
        aiPromptAr="ساعدني في اختيار رقم مميز يناسب ميزانيتي"
        aiPromptEn="Help me choose a premium number that fits my budget"
        stats={[
          { value: items.length || '—', label: isAr ? 'رقم متاح' : 'Available', icon: <Phone className="h-4 w-4" /> },
          { value: operators.length || '—', label: isAr ? 'مشغّل' : 'Operators', accent: '#E4002B' },
          { value: '24/7', label: isAr ? 'دعم ذكي' : 'AI support', accent: '#F5A962' },
        ]}
      />

      <MarketFilterShell
        isDarkMode={isDarkMode}
        isAr={isAr}
        count={filtered.length}
        total={items.length}
        unitAr="رقم"
        unitEn="numbers"
        hasFilter={hasAnyFilter}
        onClear={clearAll}
        testId="phones-filters"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-[#1E9BD9]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isAr ? 'ابحث بالرقم أو المتجر أو الوصف…' : 'Search by digits, store or description…'}
              className={`h-11 ps-9 rounded-xl ${inp}`}
              data-testid="phones-search"
            />
          </div>
          <select value={operator} onChange={(e) => changeOperator(e.target.value)} className={sel} data-testid="phones-operator">
            <option value="all">{isAr ? 'كل المشغّلين' : 'All operators'}</option>
            {operators.map((o) => (
              <option key={o.id} value={o.id}>{isAr ? o.name_ar : o.name_en}</option>
            ))}
          </select>
          <select value={code} onChange={(e) => setCode(e.target.value)} className={sel} data-testid="phones-code">
            <option value="">{isAr ? 'كل الأكواد' : 'All codes'}</option>
            {codeOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={sel} data-testid="phones-sort">
            <option value="newest">{isAr ? 'الأحدث' : 'Newest'}</option>
            <option value="price_asc">{isAr ? 'الأرخص أولاً' : 'Price ↑'}</option>
            <option value="price_desc">{isAr ? 'الأغلى أولاً' : 'Price ↓'}</option>
          </select>
          <Button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            className={`h-11 rounded-xl font-semibold whitespace-nowrap ${
              advOpen
                ? 'bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] text-white hover:opacity-90'
                : isDarkMode
                  ? 'bg-white/5 text-white border border-[#1E9BD9]/20 hover:bg-[#1E9BD9]/10'
                  : 'bg-white text-slate-700 border border-[#1E9BD9]/25 hover:bg-[#1E9BD9]/5'
            }`}
            data-testid="phones-advanced-toggle"
          >
            <SlidersHorizontal className="h-4 w-4 me-1.5" />
            {isAr ? 'فلاتر متقدمة' : 'Advanced'}
            <ChevronDown className={`h-4 w-4 ms-1.5 transition-transform ${advOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {advOpen && (
          <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`} data-testid="phones-advanced-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <FilterField label={isAr ? 'يبدأ بـ' : 'Starts with'} isDarkMode={isDarkMode}>
                <Input inputMode="numeric" value={prefix} onChange={(e) => setPrefix(e.target.value.replace(/\D/g, ''))} placeholder="050" className={`h-10 rounded-lg ${inp}`} data-testid="phones-prefix" />
              </FilterField>
              <FilterField label={isAr ? 'ينتهي بـ' : 'Ends with'} isDarkMode={isDarkMode}>
                <Input inputMode="numeric" value={suffix} onChange={(e) => setSuffix(e.target.value.replace(/\D/g, ''))} placeholder="7777" className={`h-10 rounded-lg ${inp}`} data-testid="phones-suffix" />
              </FilterField>
              <FilterField label={isAr ? 'يحتوي على (66 77)' : 'Contains (66 77)'} isDarkMode={isDarkMode}>
                <Input value={contains} onChange={(e) => setContains(e.target.value)} placeholder="66 77" className={`h-10 rounded-lg ${inp}`} data-testid="phones-contains" />
              </FilterField>
              <FilterField label={isAr ? 'النمط' : 'Pattern'} isDarkMode={isDarkMode}>
                <select value={pattern} onChange={(e) => setPattern(e.target.value)} className={`${sel} h-10 w-full rounded-lg`} data-testid="phones-pattern">
                  {patternOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </FilterField>
            </div>
          </div>
        )}
      </MarketFilterShell>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-20">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        ) : error ? (
          <p className="text-center text-red-500 py-24">{error}</p>
        ) : filtered.length === 0 ? (
          <EmptyState isDarkMode={isDarkMode} icon={Phone} title={isAr ? 'لا توجد أرقام مطابقة' : 'No matching numbers'} hint={isAr ? 'جرّب تغيير الفلاتر أو البحث بكلمات أخرى' : 'Try adjusting the filters or search terms'} />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5" data-testid="phones-grid">
            {filtered.map((it, idx) => (
              <LargeNumberCard key={it.id} item={it} index={idx} isDarkMode={isDarkMode} isAr={isAr} onClick={() => openDetails(it)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
